import type { RumInitConfiguration } from '@datadog/browser-rum';
import type { BrowserOptions } from '@sentry/react';
import type { Permission } from '../typings/permissions';
import type { ButtonType } from './components/generic';
import type { Organization, OrganizationAccessRequest, Token, User } from './types';
import type { FlagName } from '../shared/featureFlags/types';
import type { MessageOptions } from './components/settings/billing/features/consts';

type ShowPlanSelectorModalOptions = MessageOptions & {
  onHide?: () => void;
};

export type WindowUser = User & {
  on_prem: boolean;
  organization?: Organization; // Organization may be empty if the user has just registered but not set up their organization yet
  tokens: Token[];
  permissions: Permission[];
  bitbucketCloud: any;
  github: any;
  gitlab: any;
  accessRequests: OrganizationAccessRequest[];
};

export type SupportConfig =
  | { enabled: false }
  | {
    enabled: true;
    type: 'email';
    email: string;
  }
  | {
    enabled: true;
    type: 'external';
    url: string;
  };

export type Appcues = {
  identify: (userId: string, options: any) => void;
  page: () => void;
};

/**
 * Sentry config for our browser integration
 */
export type SentryConfig = Partial<{
  isEnabled: boolean;
} & Pick<BrowserOptions,
    | 'dsn' | 'tracesSampleRate' | 'tracePropagationTargets'
    | 'replaysSessionSampleRate' | 'replaysOnErrorSampleRate'
    | 'environment' | 'attachStacktrace' | 'profilesSampleRate'
  >
>;

// The standard browser `window` plus our own globals.
// TODO(leo): Get rid of all of these horrifying globals.
export type Globals = {
  user?: WindowUser;
  StatusPage: any;
  Appcues: Appcues | null;
  Sentry?: SentryConfig | null;

  /**
   * @deprecated Use `useIsSecurityEnabled` helper function instead.
   * @see ./app/helpers/features.ts
   */
  security_enabled: boolean;
  /**
   * @deprecated Use `useIsQualityEnabled` helper function instead.
   * @see ./app/helpers/features.ts
   */
  quality_policy_enabled: boolean;
  enabledFeatureFlags: Array<{ organizationId: number, flag: FlagName }>;
  enableSignupOrganizationPicker: boolean;
  _csrf: string;
  support: SupportConfig;
  fossa_version: string;
  fossa_revision: string;
  location: string;
  service_login_type: any;
  on_prem: boolean;
  customerDomain: string | 'null';
  portalUrl: string;

    /**
     * This function will open the plan selector modal that allows a user to see Subscription Plans and Features and
     * upgrade their subscriptions. Feature information needed for the optional messageOptions parameter can be found in
     * `./app/components/accounts/settings/NewBillingSettings/features/consts.ts`
     * @param {ShowPlanSelectorModalOptions} modalOptions - (Optional) Values used to display a message telling the user what subscription
     *                                          tier is needed to access a feature.
     * @param {FeatureGroup} modalOptions.featureGroup - (Optional) The type of feature that is being restricted. Refer
     *                                                   to the featureDescription object structure.
     * @param {FossaFeature} modalOptions.feature - (Optional) The identifier for the feature that is being restricted.
     * @param {FossaFeature} modalOptions.onHide - (Optional) Function to run when the modal is hidden.
     */
  showPlanSelectorModal(modalOptions?: ShowPlanSelectorModalOptions): void;
  manageSubscription(subscriptionProps): void;
  /**
   * hasPermission will return whether the currently logged in user has the specified permission.
   * @deprecated - Use the `useHasPermission` hook instead.
   */
  hasPermission(permission: Permission): boolean;

  // Why would you ever do this? 4 years later...
  confirm(title?: {}, body?: {}, onConfirm?: {}, confirmText?: {}, confirmBtnType?: ButtonType): void;
  hideConfirm(): void;
  alert(title?: {}, body?: {}): void;
  hideAlert(): void;

  oldConfirm: typeof confirm;
  oldAlert: typeof alert;

  // Prefixed browser methods
  mozRequestAnimationFrame: typeof window.requestAnimationFrame;
  msRequestAnimationFrame: typeof window.requestAnimationFrame;
  initializeClientDefaults: (user: User & { organization?: Organization }) => void;

  // These are globals from other `<script>`s that we re-export on `window`.
  heap: {
    track(event: string, properties: Record<string, string | number | boolean>): void;
    identify(userId: string): void;
    addUserProperties(properties: Record<string, string | number | undefined>): void;
  };
  rudderanalytics: {
    track(event: string, properties: Record<string, string | number | boolean>): void;
  };
  datadogRUM: Pick<RumInitConfiguration, 'applicationId' | 'clientToken' | 'site' | 'service' | 'sessionSampleRate' | 'trackUserInteractions' | 'allowedTracingUrls'> & {
    enabled: boolean;
  };
  // typings derived from https://app.delighted.com/docs/api/web#advanced-usage
  // Note this will only exist in SaaS
  delighted?: {
    survey: (
      options: DelightedSurveyOptions,
    ) => void;
  };
  devrev: {
    appId?: string;
    sessionToken?: string;
  },
  // devrev plugin on window object
  plugSDK?: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    init: ({ app_id, session_token }: { app_id: string, session_token?: string }) => void;
  };
};

export type DelightedSurveyOptions = {
  // email of the person being surveyed
  email: string,
  // name of the person being surveyed
  name: string,
  // The time when the person to survey was created. This value will be considered when showing the initial survey.
  // This should be in ISO8601 format. Formatting example (for the current time): 2022-05-26T01:12:53Z.
  createdAt: string,
  // Custom properties to associate with the survey. You can add as many properties as you need.
  properties: Record<string, unknown>,
  // The number of seconds to wait before showing a person their initial survey. Useful in cases where you want visitors to have been to your site at least some amount of time before they would be eligible to be surveyed. If you pass createdAt, that value will be used as the starting point.
  // If you want to show a survey as soon as possible, set this value to 0. There is no maximum delay.
  initialDelay?: number,
  // The number of seconds to wait before showing recurring surveys, after the initial one. Useful if you want the same person to be asked for feedback again.
  // If you pass null or false, recurring surveys will disabled. There is no maximum period.
  recurringPeriod?: number,
  // When true, skips checks we perform to avoid over-surveying.
  forceDisplay?: boolean,
  // The number of seconds to wait after the page has loaded before showing a survey. (Default: two seconds)
  minTimeOnPage?: number,
  // The number of seconds to try surveying a person. (Default: two minutes)
  // When a person chooses not to immediately respond we will re-display the survey for a period of time. Please note that the survey will not be re-displayed when it is explicitly closed by a person.
  minTimeForDisplay?: number,
};

export const window: Window & Globals = (global as any).window;

/**
 * Determine if we are running in production or not.
 * This is provided by webpack's DefinePlugin.
 *
 * Note that `process.env.NODE_ENV` only exists in prod, since
 * the webpack config for development doesn't use the DefinePlugin.
 */
export const isProduction: boolean = process.env.NODE_ENV === 'production';
